import { createTheme } from '@mui/material/styles';
import { DEFAULT_THEME_PALETTE, resolvePalette, rgba } from './pdvPalettes';

function buildSurfaces(mode, primary, ink) {
  const isDark = mode === 'dark';
  if (isDark) {
    return {
      navbarBackground: '#101C2B',
      navbarText: '#FFFFFF',
      navbarTextMuted: 'rgba(255, 255, 255, 0.7)',
      navbarBorder: 'rgba(255, 255, 255, 0.08)',
      navbarShadow: 'none',
      navbarLogo: '/logo-white.png',

      sidebarBackground: '#0D1826',
      sidebarBorder: 'rgba(255, 255, 255, 0.08)',
      sidebarDivider: 'rgba(255, 255, 255, 0.1)',
      sidebarText: '#FFFFFF',
      sidebarTextMuted: 'rgba(255, 255, 255, 0.7)',
      sidebarIcon: 'rgba(255, 255, 255, 0.75)',
      sidebarHover: 'rgba(255, 255, 255, 0.07)',
      sidebarSelected: rgba(primary, 0.24),
      sidebarSelectedHover: rgba(primary, 0.32),
      sidebarSelectedText: '#A8D8FF',
      sidebarAvatar: 'rgba(255, 255, 255, 0.16)',
      sidebarAvatarText: '#FFFFFF',
      sidebarSymbol: '/zen-symbol-white.png',

      contentBackground: '#0B1420',
      watermark: '/zen-symbol-white.png',
      watermarkOpacity: 0.05,
      logo: '/logo-white.png',
    };
  }

  return {
    navbarBackground: '#FFFFFF',
    navbarText: '#16294A',
    navbarTextMuted: '#5B6C86',
    navbarBorder: '#E4E9F1',
    navbarShadow: '0 1px 2px rgba(16, 24, 40, 0.06)',
    navbarLogo: '/logo.png',

    sidebarBackground: '#FFFFFF',
    sidebarBorder: '#E4E9F1',
    sidebarDivider: '#E4E9F1',
    sidebarText: '#16294A',
    sidebarTextMuted: '#5B6C86',
    sidebarIcon: '#5B6C86',
    sidebarHover: rgba(primary, 0.08),
    sidebarSelected: rgba(primary, 0.14),
    sidebarSelectedHover: rgba(primary, 0.2),
    sidebarSelectedText: ink,
    sidebarAvatar: rgba(primary, 0.16),
    sidebarAvatarText: ink,
    sidebarSymbol: '/zen-symbol.png',

    contentBackground: '#F4F7FB',
    watermark: '/zen-symbol.png',
    watermarkOpacity: 0.04,
    logo: '/logo.png',
  };
}

/**
 * @param {'light'|'dark'} mode
 * @param {string} [paletteName] nome da paleta PDV (ex.: "Azul")
 */
export function createZenTheme(mode, paletteName = DEFAULT_THEME_PALETTE) {
  const palette = resolvePalette(paletteName);
  const primary = palette.primary;
  const secondary = palette.secondary;
  const ink = palette.ink;
  const isDark = mode === 'dark';
  const zen = buildSurfaces(mode, primary, ink);

  const base = createTheme({
    palette: {
      mode,
      primary: { main: primary, dark: ink },
      secondary: { main: secondary },
      background: {
        default: zen.contentBackground,
        paper: isDark ? '#13202F' : '#FFFFFF',
      },
      ...(isDark
        ? {}
        : {
            text: { primary: '#16294A', secondary: '#5B6C86' },
            divider: '#E4E9F1',
          }),
    },
    shape: { borderRadius: 10 },
    zen: {
      ...zen,
      paletteName: palette.value,
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            ...(isDark ? {} : { '&.MuiPaper-outlined': { borderColor: '#E4E9F1' } }),
          },
          ...(isDark
            ? {}
            : { elevation1: { boxShadow: '0 1px 3px rgba(16, 24, 40, 0.08)' } }),
        },
      },
    },
  });

  const mobile = base.breakpoints.down('sm');

  return createTheme(base, {
    components: {
      MuiTypography: {
        styleOverrides: {
          h4: { [mobile]: { fontSize: base.typography.pxToRem(24) } },
        },
      },
      MuiPaginationItem: {
        styleOverrides: {
          root: {
            [mobile]: {
              minWidth: 30,
              height: 30,
              margin: '0 1px',
              fontSize: base.typography.pxToRem(13),
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            [base.breakpoints.down('md')]: { padding: base.spacing(1, 1.25) },
          },
        },
      },
    },
  });
}

/** @deprecated use createZenTheme(mode, palette) */
export const ZEN_PRIMARY = resolvePalette(DEFAULT_THEME_PALETTE).primary;
export const ZEN_SECONDARY = resolvePalette(DEFAULT_THEME_PALETTE).secondary;
